import axios from 'axios';

const API_BASE_URL = process.env.API_BASE_URL || 'http://localhost:5000/api';

const api = axios.create({
  baseURL: API_BASE_URL,
  headers: {
    'Content-Type': 'application/json',
  },
});

// ── Żywa sesja MC ──────────────────────────────────────────────────────────────
// Token SSO Lidla żyje ~5 minut, więc kopia sesji zapisana w bazie jest nieświeża
// niemal zawsze — a backend (_resolve_session) woli sesję z nagłówka X-MC-Session
// i dopiero w jej braku schodzi do bazy. Bez tego nagłówka cała ta gałąź była
// martwa i mc-recover/mc-push jechały na wygasłej kopii („Sesja MC wygasła").
// Sesję wstrzykuje App.jsx za każdym razem, gdy wtyczka odda świeże ciasteczka.
let liveMcSession = null;

export const setLiveMcSession = (session) => {
  liveMcSession = (session && session.wp_logged_in_value && session.lidl_sso_token)
    ? session
    : null;
};

// btoa przewraca się na znakach spoza Latin-1, a ciasteczka MC bywają URL-enkodowane
const encodeSession = (session) =>
  btoa(unescape(encodeURIComponent(JSON.stringify(session))));

api.interceptors.request.use(
  (config) => {
    const token = localStorage.getItem('token');
    if (token) {
      config.headers.Authorization = `Bearer ${token}`;
    }
    // Tylko endpointy AI rozmawiają z MC — nie ma po co obciążać reszty ~2 KB nagłówka
    if (liveMcSession && (config.url || '').startsWith('/ai/')) {
      try {
        config.headers['X-MC-Session'] = encodeSession(liveMcSession);
      } catch {
        // Sesja nie do zakodowania — backend użyje kopii z bazy
      }
    }
    return config;
  },
  (error) => Promise.reject(error)
);

api.interceptors.response.use(
  (response) => response,
  (error) => {
    if (error.response?.status === 401) {
      const token = localStorage.getItem('token');
      const url = error.config?.url || '';
      if (token && !url.includes('/auth/login') && !url.includes('/auth/register')) {
        localStorage.removeItem('token');
        localStorage.removeItem('user');
        window.location.href = '/';
      }
    }
    return Promise.reject(error);
  }
);

export const authAPI = {
  register:         (userData)          => api.post('/auth/register', userData),
  login:            (credentials)       => api.post('/auth/login', credentials),
  profile:          ()                  => api.get('/auth/profile'),
  updateProfile:    (data)              => api.put('/auth/profile', data),
  refresh:          ()                  => api.post('/auth/refresh'),
  forgotPassword:   (email)             => api.post('/auth/forgot-password', { email }),
  verifyResetCode:  (email, code, pass) => api.post('/auth/verify-reset-code', { email, code, new_password: pass }),
  deleteAccount:    (password)          => api.delete('/auth/account', { data: { password } }),
  logout: () => {
    localStorage.removeItem('token');
    localStorage.removeItem('user');
  },
};

export const aiAPI = {
  saveMCSession: (cookieString) => api.post('/ai/mc-session', { cookie_string: cookieString }),
  getMCSession: () => api.get('/ai/mc-session'),
  deleteMCSession: () => api.delete('/ai/mc-session'),
  parseRecipe: (text) => api.post('/ai/parse-recipe', { text }),
  getConversionStatus: () => api.get('/ai/conversion-status'),
  getSavedRecipes: () => api.get('/ai/saved-recipes'),
  saveRecipe: (data) => api.post('/ai/saved-recipes', data),
  updateSavedRecipe: (id, data) => api.put(`/ai/saved-recipes/${id}`, data),
  deleteSavedRecipe: (id) => api.delete(`/ai/saved-recipes/${id}`),
  pushRecipeToMC: (mcPayload, lang = 'pl') => api.post('/ai/mc-push', { mc_payload: mcPayload, lang }),
  // Wtyczka abortuje fetch do MC po 12 s, a MC potrafi utworzyć przepis mimo to —
  // wtedy szukamy go po tytule na liście własnych przepisów i odzyskujemy ID
  recoverMcPush: (title, mcPayload, lang = 'pl', withinMinutes = 30) =>
    api.post('/ai/mc-recover', { title, mc_payload: mcPayload, lang, within_minutes: withinMinutes }),
  rebuildMcPayload: (recipe, lang = 'pl') => api.post('/ai/mc-rebuild-payload', { recipe, lang }),
  importUrl: (url) => api.post('/ai/import-url', { url }),
  ocrImage: (file) => {
    const fd = new FormData();
    fd.append('image', file);
    return api.post('/ai/ocr-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  // Zdjęcie potrawy → miniatura przepisu na MC. Upload jest osobny od wysyłki:
  // najpierw pliki lądują w mediach MC (mc-image), a ID podpinamy pod przepis
  // dopiero gdy już powstał (mc-attach-image).
  uploadMcImage: (file, lang = 'pl') => {
    const fd = new FormData();
    fd.append('image', file);
    fd.append('lang', lang);
    return api.post('/ai/mc-image', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  attachMcImage: (recipeId, mcPayload, media, lang = 'pl') =>
    api.post('/ai/mc-attach-image', { recipe_id: recipeId, mc_payload: mcPayload, media, lang }),
  // Wysyłka w tle: serwer sam wysyła, dopytuje MC i domyka publikację, więc kartę
  // można zamknąć. Zwraca job_id do odpytywania o stan.
  pushMcAsync: (mcPayload, lang = 'pl', media = null) =>
    api.post('/ai/mc-push-async', { mc_payload: mcPayload, lang, media }),
  mcPushStatus: (jobId) => api.get(`/ai/mc-push-status/${jobId}`),
};

export const adminAPI = {
  getStats:            ()           => api.get('/admin/stats'),
  listUsers:           ()           => api.get('/admin/users'),
  createUser:          (data)       => api.post('/admin/users', data),
  updateUser:          (id, data)   => api.put(`/admin/users/${id}`, data),
  deleteUser:          (id)         => api.delete(`/admin/users/${id}`),
  resetPassword:       (id, pass)   => api.put(`/admin/users/${id}/reset-password`, { new_password: pass }),
  resetConversions:    (id)         => api.post(`/admin/users/${id}/reset-conversions`),
  getUserRecipes:      (id)         => api.get(`/admin/users/${id}/recipes`),
  deleteRecipe:        (id)         => api.delete(`/admin/recipes/${id}`),
  exportRecipes:       ()           => api.get('/admin/recipes-export'),
  importRecipes:       (data, mode) => api.post(`/admin/recipes-import?mode=${mode}`, data),
};

export const mcAPI = {
  convertRecipe: async (data, onProgress) => {
    const token = localStorage.getItem('token');
    const base = process.env.API_BASE_URL || 'http://localhost:5000/api';
    const res = await fetch(`${base}/ai/mc-convert`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });

    if (!res.ok) {
      const text = await res.text();
      return { success: false, error: `Serwer zwrócił ${res.status}: ${text.slice(0, 200)}` };
    }

    const reader = res.body.getReader();
    const decoder = new TextDecoder();
    let buffer = '';

    while (true) {
      const { done, value } = await reader.read();
      if (done) break;
      buffer += decoder.decode(value, { stream: true });
      const blocks = buffer.split('\n\n');
      buffer = blocks.pop();
      for (const block of blocks) {
        const line = block.split('\n').find(l => l.startsWith('data: '));
        if (!line) continue;
        try {
          const event = JSON.parse(line.slice(6));
          if (onProgress) onProgress(event);
          if (event.stage === 'done') return event.result;
          if (event.stage === 'error') {
            return { success: false, error: event.message, limit_reached: event.limit_reached };
          }
        } catch {}
      }
    }

    return { success: false, error: 'Nieprawidłowa odpowiedź serwera' };
  },
  repairRecipe: async (data) => {
    const token = localStorage.getItem('token');
    const base = process.env.API_BASE_URL || 'http://localhost:5000/api';
    const res = await fetch(`${base}/ai/mc-repair`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    const text = await res.text();
    try { return JSON.parse(text); }
    catch { return { success: false, error: `Serwer zwrócił ${res.status}: ${text.slice(0, 200)}` }; }
  },
  fixSteps: async (data) => {
    const token = localStorage.getItem('token');
    const base = process.env.API_BASE_URL || 'http://localhost:5000/api';
    const res = await fetch(`${base}/ai/mc-fix-steps`, {
      method: 'POST',
      headers: {
        'Content-Type': 'application/json',
        ...(token ? { Authorization: `Bearer ${token}` } : {}),
      },
      body: JSON.stringify(data),
    });
    const text = await res.text();
    try {
      return JSON.parse(text);
    } catch {
      return { success: false, error: `Serwer zwrócił ${res.status}: ${text.slice(0, 200)}` };
    }
  },
};

export const setAuthToken = (token) => {
  localStorage.setItem('token', token);
};

export const getCurrentUser = () => {
  const userStr = localStorage.getItem('user');
  return userStr ? JSON.parse(userStr) : null;
};

export const setCurrentUser = (user) => {
  localStorage.setItem('user', JSON.stringify(user));
};

export default api;
